// ============================================================
// THE ARMOURY'S KINDS (plan armoury, stages 4-5) - weapons, effects, area
// templates, loadouts, attack modifiers and the forge's modules kept in the
// Assets library.
//
// Rows of the same table as `kinds.ts`'s (`KindRow`), composed there. All
// five share ONE file envelope:
//
//   { kind: 'deathwatch-armoury', type: 'weapon', version: 1, content }
//
// so an upload finds its row by `type`, an export is the stored text as it
// is, and a file from a later build (`version` above ours) is refused
// with a sentence rather than misread.
//
// None copies into a session from the Assets page: a weapon is put on a
// sheet from the bench or the character editor, an effect or a template
// is picked from a shelf, a modifier from the attack's list.
//
// Pure. Zero React dependency.
// ============================================================
import { emptyArea, type WeaponArea } from '../areaShapes';
import { emptyWeapon } from '../character/factories';
import type { CharacterWeapon } from '../character/types';
import type { AfflictionSpec } from '../afflictions';
import type { ExposureSpec } from '../exposureEffects';
import { defaultHallucinogen, type HallucinogenSpec } from '../hallucination';
import type { Loadout } from '../roster/types';
import { readWeaponRules } from '../weaponRules';
import type { EffectKind } from '../weaponEffectList';
import { readModule, type Module } from '../weaponEditor/modules';
import { readAlwaysOn, readSoloModes, type EffectItem, type SoloMode } from '../character/soloModes';
import type { KindFacts } from './fileKinds';
import type { ThumbShot } from './thumbScene';
import { blankTactics, readTactics, type Tactics } from '../killTeam/tactics';

export const ARMOURY_FILE_KIND = 'deathwatch-armoury';
const FILE_VERSION = 1;

interface EffectContent {
  kind: EffectKind;
  spec: AfflictionSpec | ExposureSpec | HallucinogenSpec;
}
interface AreaContent {
  area: WeaponArea;
}
interface ModifierContent {
  label: string;
  value: number;
}

/** What each Armoury kind's content is, decoded. */
export interface ArmouryContent {
  weapon: CharacterWeapon;
  effect: EffectContent;
  area: AreaContent;
  loadout: Loadout;
  modifier: ModifierContent;
  module: Module;
  tactics: Tactics;
  alwaysOn: EffectItem;
  soloMode: SoloMode;
}
export type ArmouryKind = keyof ArmouryContent;

type Decoded<T> = { content: T; error?: undefined } | { content?: undefined; error: string };

/** A row as `kinds.ts` composes it: the same members as its `KindRow`. */
export interface ArmouryRow<K extends ArmouryKind> extends KindFacts {
  kind: K;
  encode(content: ArmouryContent[K]): string;
  decode(text: string): Decoded<ArmouryContent[K]>;
  claims(data: unknown): boolean;
  nameOf(content: ArmouryContent[K], fallback?: string): string;
  blank(name: string, id: string): ArmouryContent[K];
  fresh?(content: ArmouryContent[K], id: string): ArmouryContent[K];
  /** None: an Armoury card keeps its typographic drawing (`AssetGlyph`), never a picture of a weapon. */
  shot(content: ArmouryContent[K]): ThumbShot | null;
}

const isRecord = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);

function parse(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

const EFFECT_KINDS: readonly EffectKind[] = ['affliction', 'exposure', 'hallucinogen'];

/** Each kind's content off its envelope: the content this build can use, or null. */
const READ: { [K in ArmouryKind]: (raw: unknown) => ArmouryContent[K] | null } = {
  weapon: (raw) => {
    if (!isRecord(raw) || typeof raw.name !== 'string' || (raw.type !== 'melee' && raw.type !== 'ranged')) return null;
    const rules = readWeaponRules(raw.rules);
    const { rules: _stored, ...rest } = raw as unknown as CharacterWeapon;
    return { ...emptyWeapon(String(raw.id ?? 'weapon')), ...rest, ...(rules.length ? { rules } : {}) };
  },
  effect: (raw) =>
    isRecord(raw) && EFFECT_KINDS.includes(raw.kind as EffectKind) && isRecord(raw.spec)
      ? { kind: raw.kind as EffectKind, spec: raw.spec as unknown as EffectContent['spec'] }
      : null,
  area: (raw) =>
    isRecord(raw) && isRecord(raw.area) && typeof raw.area.shape === 'string'
      ? { area: raw.area as unknown as WeaponArea }
      : null,
  loadout: (raw) =>
    isRecord(raw) && typeof raw.name === 'string' && Array.isArray(raw.weapons)
      ? ({ tags: [], createdAt: 0, updatedAt: 0, ...raw } as unknown as Loadout)
      : null,
  module: (raw) => readModule(raw),
  tactics: (raw) => readTactics(raw),
  // A library item is its bundle alone: an origin is what a SHEET's copy carries, never stored in the item.
  alwaysOn: (raw) => readAlwaysOn([raw]).map(({ origin: _o, ...item }) => item)[0] ?? null,
  soloMode: (raw) => readSoloModes([raw]).map(({ origin: _o, ...mode }) => mode)[0] ?? null,
  modifier: (raw) =>
    isRecord(raw) && typeof raw.label === 'string' && typeof raw.value === 'number' && Number.isFinite(raw.value)
      ? { label: raw.label, value: raw.value }
      : null,
};

/** "a weapon", "an effect": the article the refusal sentences take. */
const A = (word: string) => (/^[aeiou]/i.test(word) ? `an ${word}` : `a ${word}`);

/** The common half of every Armoury row: the envelope, its claim, its refusals. */
function envelope<K extends ArmouryKind>(kind: K, word: string) {
  return {
    kind,
    copiesIntoSession: false,
    claims: (data: unknown) => isRecord(data) && data.kind === ARMOURY_FILE_KIND && data.type === kind,
    encode: (content: ArmouryContent[K]) =>
      JSON.stringify({ kind: ARMOURY_FILE_KIND, type: kind, version: FILE_VERSION, content }),
    decode: (text: string): Decoded<ArmouryContent[K]> => {
      const data = parse(text);
      if (isRecord(data) && data.kind === ARMOURY_FILE_KIND && Number(data.version) > FILE_VERSION) {
        return { error: `That ${word} was made by a newer version of the app.` };
      }
      const content = isRecord(data) && data.type === kind ? READ[kind](data.content) : null;
      return content ? { content } : { error: `That file does not contain ${A(word)}.` };
    },
  };
}

const WEAPON: ArmouryRow<'weapon'> = {
  ...envelope('weapon', 'weapon'),
  label: 'Weapon',
  plural: 'Weapons',
  open: 'Open on the bench',
  nameOf: (w) => w.name,
  blank: (name, id) => ({ ...emptyWeapon(id), name }),
  // A weapon's id is its identity on a sheet: a duplicate needs its own.
  fresh: (w, id) => ({ ...w, id }),
  shot: () => null,
};

const EFFECT: ArmouryRow<'effect'> = {
  ...envelope('effect', 'effect'),
  label: 'Effect',
  plural: 'Effects',
  // Picked from a weapon's Effects shelf; edited where it is used.
  open: null,
  nameOf: (_c, fallback = 'Effect') => fallback,
  blank: () => ({ kind: 'hallucinogen', spec: defaultHallucinogen() }),
  shot: () => null,
};

const AREA: ArmouryRow<'area'> = {
  ...envelope('area', 'area template'),
  label: 'Area template',
  plural: 'Area templates',
  open: null,
  nameOf: (_c, fallback = 'Area template') => fallback,
  blank: () => ({ area: { ...emptyArea(), shape: 'blast' } }),
  shot: () => null,
};

const LOADOUT: ArmouryRow<'loadout'> = {
  ...envelope('loadout', 'loadout'),
  label: 'Loadout',
  plural: 'Loadouts',
  open: null,
  nameOf: (l) => l.name,
  blank: (name, id) => ({ id, name, weapons: [], tags: [], createdAt: 0, updatedAt: 0 }),
  fresh: (l, id) => ({ ...l, id }),
  shot: () => null,
};

const MODIFIER: ArmouryRow<'modifier'> = {
  ...envelope('modifier', 'modifier'),
  label: 'Modifier',
  plural: 'Modifiers',
  open: null,
  nameOf: (m) => m.label,
  blank: (name) => ({ label: name, value: 10 }),
  shot: () => null,
};

const MODULE: ArmouryRow<'module'> = {
  ...envelope('module', 'module'),
  label: 'Module',
  plural: 'Modules',
  open: 'Open the module',
  nameOf: (m) => m.name,
  blank: (name) => ({ name, changes: [{ field: 'attackMod', op: 'add', value: 5 }] }),
  shot: () => null,
};

/**
 * Tactics (Squad Mode stage 5): the table's squad abilities and the Oaths that unlock them, in the Armoury's
 * envelope. Its ids name its abilities and Oaths, so a duplicate keeps them and takes only a new identity.
 */
const TACTICS: ArmouryRow<'tactics'> = {
  ...envelope('tactics', 'Tactics item'),
  // Never copied into a session (plan modes-rework): a sheet carries its Oath, copied whole with its origin.
  copiesIntoSession: false,
  label: 'Tactics',
  plural: 'Tactics',
  open: 'Open the Tactics',
  nameOf: (t) => t.name,
  blank: (name, id) => blankTactics(name, id),
  fresh: (t, id) => ({ ...t, id }),
  shot: () => null,
};

/**
 * Always on and Solo mode (plan modes-rework): one bundle of effects each, copied onto a sheet with its origin. An
 * Always on item may carry a trigger; a Solo mode its one-at-a-time group. A duplicate takes a new identity only.
 */
function bundleRow<K extends 'alwaysOn' | 'soloMode'>(kind: K, word: string, label: string, plural: string) {
  return {
    ...envelope(kind, word),
    label,
    plural,
    open: `Open the ${word}`,
    nameOf: (b: ArmouryContent[K]) => b.name,
    blank: (name: string, id: string) => ({ id, name, effects: [] }) as ArmouryContent[K],
    fresh: (b: ArmouryContent[K], id: string) => ({ ...b, id }),
    shot: () => null,
  };
}

const ALWAYS_ON: ArmouryRow<'alwaysOn'> = bundleRow('alwaysOn', 'Always on item', 'Always on', 'Always on');
const SOLO_MODE: ArmouryRow<'soloMode'> = bundleRow('soloMode', 'Solo mode', 'Solo mode', 'Solo modes');

/** The rows, for `kinds.ts` to compose. */
export const ARMOURY_ROWS: { [K in ArmouryKind]: ArmouryRow<K> } = {
  weapon: WEAPON,
  effect: EFFECT,
  area: AREA,
  loadout: LOADOUT,
  modifier: MODIFIER,
  module: MODULE,
  tactics: TACTICS,
  alwaysOn: ALWAYS_ON,
  soloMode: SOLO_MODE,
};
